import React, { createContext, useContext, useState, useCallback } from 'react';

// Create the Comic Context
const ComicContext = createContext();

// Helper function to create an empty page
const createEmptyPage = (pageNumber) => ({
  pageNumber,
  bgImage: null,
  ptImage: null,
  lbImage: null,
  ptVisible: true,
  lbVisible: true,
  hotspots: [],
  positionsData: [],
});

const API_BASE_URL_CTX = '/api';

// Helper function to create an empty comic
const createEmptyComic = (metadata) => {
  const pages = {};
  // Page 0 is the cover; pages 1..totalPages are content pages
  for (let i = 0; i <= metadata.totalPages; i++) {
    pages[i] = createEmptyPage(i);
  }
  return {
    id: Date.now().toString(),
    metadata: {
      title: metadata.title,
      author: metadata.author,
      totalPages: metadata.totalPages,
      category: metadata.category || '',
      languages: metadata.languages || {
        portugues: true,
        libras: true,
        english: false,
        asl: false,
      },
      schemaVersion: 2,
    },
    pages,
    currentPage: 0,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  };
};

// Migrate old comics (1-indexed pages) to new format (0-indexed, page 0 = cover).
// Old page 1 → new page 0, old page 2 → new page 1, …, old page N → new page N-1, new page N = empty.
const migrateComicToV2 = async (comicId, metadata, pages) => {
  const N = metadata.totalPages;
  const newPages = {};

  for (let i = 0; i < N; i++) {
    const src = pages[i + 1] || createEmptyPage(i);
    newPages[i] = { ...src, pageNumber: i };
  }
  newPages[N] = createEmptyPage(N);

  // Persist each page to backend at the new index
  for (let i = 0; i <= N; i++) {
    try {
      await fetch(`${API_BASE_URL_CTX}/comics/${comicId}/pages/${i}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bgImage: newPages[i].bgImage,
          ptImage: newPages[i].ptImage,
          lbImage: newPages[i].lbImage,
          ptVisible: newPages[i].ptVisible,
          lbVisible: newPages[i].lbVisible,
          hotspots: newPages[i].hotspots || [],
          positionsData: newPages[i].positionsData || [],
        }),
      });
    } catch (e) {
      console.warn(`Migration: failed to save page ${i} for comic ${comicId}`, e);
    }
  }

  // Mark comic as migrated in backend
  try {
    await fetch(`${API_BASE_URL_CTX}/comics/${comicId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ metadata: { ...metadata, schemaVersion: 2 } }),
    });
  } catch (e) {
    console.warn('Migration: failed to update schemaVersion', e);
  }

  console.log(`Migration complete for comic ${comicId}`);
  return newPages;
};

// ComicProvider component
export const ComicProvider = ({ children }) => {
  const [comics, setComics] = useState([]);
  const [currentComicId, setCurrentComicId] = useState(null);
  const [isLoadingComics, setIsLoadingComics] = useState(false);
  const [isCreatingComic, setIsCreatingComic] = useState(false);
  const [isDeletingComic, setIsDeletingComic] = useState(false);

  // Get current comic
  const getCurrentComic = useCallback(() => {
    return comics.find(c => c.id === currentComicId);
  }, [comics, currentComicId]);

  // Get current page
  const getCurrentPage = useCallback(() => {
    const comic = getCurrentComic();
    if (!comic) return null;
    return comic.pages[comic.currentPage];
  }, [getCurrentComic]);

  // Create a new comic
  const createComic = useCallback(async (metadata) => {
    setIsCreatingComic(true);
    const newComic = createEmptyComic(metadata);
    
    try {
      // Save to backend first
      const response = await fetch(`${API_BASE_URL_CTX}/comics`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: newComic.id,
          metadata: newComic.metadata
        })
      });
      
      if (!response.ok) {
        throw new Error(`Failed to create comic in backend: ${response.status}`);
      }
      
      const backendComic = await response.json();
      // Use the comic from backend (in case backend modifies it)
      const finalComic = {
        ...newComic,
        id: backendComic.id,
        createdAt: backendComic.createdAt,
        updatedAt: backendComic.updatedAt
      };
      
      setComics(prevComics => [...prevComics, finalComic]);
      setCurrentComicId(finalComic.id);
      return finalComic;
    } catch (error) {
      console.error('Error creating comic in backend:', error);
      // Fallback to local creation only
      console.warn('Falling back to local comic creation only');
      setComics(prevComics => [...prevComics, newComic]);
      setCurrentComicId(newComic.id);
      return newComic;
    } finally {
      setIsCreatingComic(false);
    }
  }, []);

  // Load a comic by ID
  const loadComic = useCallback((comicId) => {
    const comic = comics.find(c => c.id === comicId);
    if (comic) {
      setCurrentComicId(comicId);
      return comic;
    }
    return null;
  }, [comics]);

  // Update current page data
  const updateCurrentPage = useCallback((pageData) => {
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === currentComicId) {
          return {
            ...comic,
            pages: {
              ...comic.pages,
              [comic.currentPage]: {
                ...comic.pages[comic.currentPage],
                ...pageData,
              },
            },
            updatedAt: new Date().toISOString(),
          };
        }
        return comic;
      })
    );
  }, [currentComicId]);

  // Update a specific page by page number (for race condition prevention)
  const updateSpecificPage = useCallback((pageNumber, pageData) => {
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === currentComicId) {
          return {
            ...comic,
            pages: {
              ...comic.pages,
              [pageNumber]: {
                ...comic.pages[pageNumber],
                ...pageData,
              },
            },
            updatedAt: new Date().toISOString(),
          };
        }
        return comic;
      })
    );
  }, [currentComicId]);

  // Navigation functions
  const goToFirstPage = useCallback(() => {
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === currentComicId) {
          return { ...comic, currentPage: 0 };
        }
        return comic;
      })
    );
  }, [currentComicId]);

  const goToPreviousPage = useCallback(() => {
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === currentComicId) {
          const newPage = Math.max(0, comic.currentPage - 1);
          return { ...comic, currentPage: newPage };
        }
        return comic;
      })
    );
  }, [currentComicId]);

  const goToNextPage = useCallback(() => {
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === currentComicId) {
          const newPage = Math.min(comic.metadata.totalPages, comic.currentPage + 1);
          return { ...comic, currentPage: newPage };
        }
        return comic;
      })
    );
  }, [currentComicId]);

  const goToLastPage = useCallback(() => {
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === currentComicId) {
          return { ...comic, currentPage: comic.metadata.totalPages };
        }
        return comic;
      })
    );
  }, [currentComicId]);

  // Delete a comic
  const deleteComic = useCallback(async (comicId) => {
    setIsDeletingComic(true);
    try {
      // Update local state first for immediate UI feedback
      setComics(prevComics => {
        const updatedComics = prevComics.filter(c => c.id !== comicId);
        // Save to localStorage with updated comics
        try {
          localStorage.setItem('balum_comics', JSON.stringify(updatedComics));
        } catch (error) {
          console.error('Error saving to localStorage:', error);
        }
        return updatedComics;
      });
      
      if (currentComicId === comicId) {
        setCurrentComicId(null);
      }
      
      // Sync with backend
      try {
        const response = await fetch(`${API_BASE_URL_CTX}/comics/${comicId}`, {
          method: 'DELETE'
        });
        
        if (!response.ok) {
          console.warn(`Failed to delete comic from backend: ${response.status}`);
          // Note: We've already removed it locally, so we can't easily revert
          // In a production app, you might want to implement a retry mechanism
        }
      } catch (backendError) {
        console.warn('Failed to sync delete with backend:', backendError);
      }
    } finally {
      setIsDeletingComic(false);
    }
  }, [currentComicId]);

  // Update comic category
  const updateComicCategory = useCallback(async (comicId, newCategory) => {
    // Update local state first for immediate UI feedback
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === comicId) {
          return {
            ...comic,
            metadata: {
              ...comic.metadata,
              category: newCategory,
            },
            updatedAt: new Date().toISOString(),
          };
        }
        return comic;
      })
    );
    
    // Save to localStorage
    try {
      const updatedComics = comics.map(comic => 
        comic.id === comicId 
          ? {...comic, metadata: {...comic.metadata, category: newCategory}, updatedAt: new Date().toISOString()}
          : comic
      );
      localStorage.setItem('balum_comics', JSON.stringify(updatedComics));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
    
    // Sync with backend
    try {
      const comicToUpdate = comics.find(c => c.id === comicId);
      if (comicToUpdate) {
        const response = await fetch(`${API_BASE_URL_CTX}/comics/${comicId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            metadata: {
              ...comicToUpdate.metadata,
              category: newCategory
            }
          })
        });
        
        if (!response.ok) {
          console.warn(`Failed to update comic in backend: ${response.status}`);
          // We could revert the local change here, but for now just log the warning
        }
      }
    } catch (backendError) {
      console.warn('Failed to sync category update with backend:', backendError);
    }
  }, [comics]);

  // Update comic metadata fields (title, author, totalPages, etc.)
  const updateComicMetadata = useCallback(async (comicId, updatedFields) => {
    setComics(prevComics =>
      prevComics.map(comic => {
        if (comic.id === comicId) {
          return {
            ...comic,
            metadata: { ...comic.metadata, ...updatedFields },
            updatedAt: new Date().toISOString(),
          };
        }
        return comic;
      })
    );

    try {
      const updatedComics = comics.map(comic =>
        comic.id === comicId
          ? { ...comic, metadata: { ...comic.metadata, ...updatedFields }, updatedAt: new Date().toISOString() }
          : comic
      );
      localStorage.setItem('balum_comics', JSON.stringify(updatedComics));
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }

    try {
      const comicToUpdate = comics.find(c => c.id === comicId);
      if (comicToUpdate) {
        const response = await fetch(`${API_BASE_URL_CTX}/comics/${comicId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ metadata: { ...comicToUpdate.metadata, ...updatedFields } })
        });
        if (!response.ok) {
          console.warn(`Failed to update comic metadata in backend: ${response.status}`);
        }
      }
    } catch (backendError) {
      console.warn('Failed to sync metadata update with backend:', backendError);
    }
  }, [comics]);

  // Clear current comic (useful when navigating away)
  const clearCurrentComic = useCallback(() => {
    setCurrentComicId(null);
  }, []);

  // Save all comics to localStorage
  const saveToLocalStorage = useCallback((comicsToSave = null) => {
    try {
      // Use provided comics array or current state
      const comicsToStore = comicsToSave !== null ? comicsToSave : comics;
      localStorage.setItem('balum_comics', JSON.stringify(comicsToStore));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }, [comics]);

  // Load all comics - try backend first, then localStorage as fallback
  const loadFromLocalStorage = useCallback(async () => {
    setIsLoadingComics(true);
    try {
      // First try to fetch from backend
      const response = await fetch(`${API_BASE_URL_CTX}/comics`);
      if (response.ok) {
        const backendComics = await response.json();
        
        // Transform backend format to frontend format
        const transformedComics = await Promise.all(backendComics.map(async (backendComic) => {
          // Create empty page structures for each comic
          // Page 0 is the cover; pages 1..totalPages are content pages
          const pages = {};
          for (let i = 0; i <= backendComic.metadata.totalPages; i++) {
            pages[i] = createEmptyPage(i);
          }

          // Try to load page data from backend for each page
          // This ensures pages have their actual data if it exists
          for (let i = 0; i <= backendComic.metadata.totalPages; i++) {
            try {
              const pageResponse = await fetch(`${API_BASE_URL_CTX}/comics/${backendComic.id}/pages/${i}`);
              if (pageResponse.ok) {
                const pageData = await pageResponse.json();
                pages[i] = {
                  pageNumber: i,
                  bgImage: pageData.bgImage || null,
                  ptImage: pageData.ptImage || null,
                  lbImage: pageData.lbImage || null,
                  ptVisible: pageData.ptVisible !== false,
                  lbVisible: pageData.lbVisible !== false,
                  hotspots: pageData.hotspots || [],
                  positionsData: pageData.positionsData || []
                };
              }
            } catch (pageError) {
              console.warn(`Failed to load page ${i} for comic ${backendComic.id}:`, pageError);
            }
          }

          // Migrate old comics that still use 1-indexed pages (no schemaVersion)
          let finalPages = pages;
          if (!backendComic.metadata.schemaVersion || backendComic.metadata.schemaVersion < 2) {
            console.log(`Migrating comic ${backendComic.id} to schema v2…`);
            finalPages = await migrateComicToV2(backendComic.id, backendComic.metadata, pages);
          }

          return {
            id: backendComic.id,
            metadata: {
              title: backendComic.metadata.title,
              author: backendComic.metadata.author,
              totalPages: backendComic.metadata.totalPages,
              category: backendComic.metadata.category || '',
              languages: backendComic.metadata.languages || {
                portugues: true,
                libras: true,
                english: false,
                asl: false,
              },
              status: backendComic.metadata.status || 'development',
              schemaVersion: 2,
            },
            pages: finalPages,
            currentPage: 0,
            createdAt: backendComic.createdAt,
            updatedAt: backendComic.updatedAt
          };
        }));
        
        // Save to localStorage as cache
        try {
          localStorage.setItem('balum_comics', JSON.stringify(transformedComics));
        } catch (storageError) {
          console.error('Error saving to localStorage:', storageError);
        }
        
        setComics(transformedComics);
        return transformedComics;
      } else {
        throw new Error(`Backend returned ${response.status}`);
      }
    } catch (backendError) {
      console.warn('Failed to fetch from backend, falling back to localStorage:', backendError);
      
      // Fallback to localStorage
      try {
        const stored = localStorage.getItem('balum_comics');
        if (stored) {
          const loadedComics = JSON.parse(stored);
          setComics(loadedComics);
          return loadedComics;
        }
      } catch (localStorageError) {
        console.error('Error loading from localStorage:', localStorageError);
      }
      
      return [];
    } finally {
      setIsLoadingComics(false);
    }
  }, []);

  const value = {
    // State
    comics,
    currentComicId,
    currentComic: getCurrentComic(),
    currentPage: getCurrentPage(),
    
    // Loading states
    isLoadingComics,
    isCreatingComic,
    isDeletingComic,

    // Comic operations
    createComic,
    loadComic,
    deleteComic,
    clearCurrentComic,
    updateComicCategory,  // Updated to use category instead of filter
    updateComicMetadata,

    // Page operations
    updateCurrentPage,
    updateSpecificPage,

    // Navigation
    goToFirstPage,
    goToPreviousPage,
    goToNextPage,
    goToLastPage,

    // Storage
    saveToLocalStorage,
    loadFromLocalStorage,
  };

  return (
    <ComicContext.Provider value={value}>
      {children}
    </ComicContext.Provider>
  );
};

// Custom hook to use the Comic Context
export const useComic = () => {
  const context = useContext(ComicContext);
  if (!context) {
    throw new Error('useComic must be used within a ComicProvider');
  }
  return context;
};
